import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import MarketingLayout from './MarketingLayout'
import Seo from './Seo'
import StoreBadges from './StoreBadges'
import ChevronDown from '../../Assets/landing/Chevron_Down.png'
import freeIcon from '../../Assets/billing/free.png'
import diamondIcon from '../../Assets/billing/diamond.png'
import everAfterIcon from '../../Assets/billing/ever-after.png'
import '../AppLandingPage.css'
import './Marketing.css'

const COMPARISON_ROWS = [
    { feature: 'Guest list', free: true, premium: true },
    { feature: 'Table & seating planner', free: true, premium: true },
    { feature: 'Menu & meal planner', free: true, premium: true },
    { feature: 'Planning checklist & to-dos', free: true, premium: true },
    { feature: 'Wedding day schedule', free: true, premium: true },
    { feature: 'Digital save the dates', free: true, premium: true },
    { feature: 'Personalised wedding website', free: false, premium: true },
    { feature: 'Digital RSVPs & meal choices', free: false, premium: true },
    { feature: 'Guest photo album', free: false, premium: true },
    { feature: 'Wedding website QR code', free: false, premium: true },
]

const PRICING_FAQS = [
    {
        q: 'Can I really plan my wedding for free?',
        a: 'Yes. The free plan includes your guest list, table planner, menu planner, day schedule, checklist and digital save the dates — everything you need to get properly started. Premium plans add your live wedding website, digital RSVPs, guest photo album and QR code.',
    },
    {
        q: 'What’s the difference between Diamond and Ever After?',
        a: 'They unlock the same premium features. Diamond is a flexible monthly subscription you can cancel any time. Ever After is a single one-off payment that keeps premium access from planning right through to six months after your wedding day.',
    },
    {
        q: 'Can I cancel Diamond whenever I want?',
        a: 'Yes — Diamond is a monthly plan with no lock-in. Turn off auto-renew any time and you keep access until the end of your billing period.',
    },
    {
        q: 'How do I upgrade?',
        a: 'Download the app, create your free account, then upgrade from Settings → Billing whenever you’re ready. You can also manage your subscription on the web at firstdance.app/billing.',
    },
]

/** /pricing — public plan comparison with live prices from the server. */
export default function Pricing() {
    const [pricing, setPricing] = useState(null)
    const [openFaqIndex, setOpenFaqIndex] = useState(null)

    useEffect(() => {
        window.scrollTo(0, 0)

        axios
            .get(`${process.env.REACT_APP_API}/subscriptions/public-pricing`)
            .then((res) => setPricing(res.data))
            .catch((error) => {
                // Cards fall back to copy without amounts
                console.log('Unable to load pricing', error)
            })
    }, [])

    const toggleFaq = (index) => {
        setOpenFaqIndex((prev) => (prev === index ? null : index))
    }

    const diamondPrice = pricing?.diamond?.amount
    const everafterPrice = pricing?.everafter?.amount

    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: PRICING_FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
    }

    return (
        <MarketingLayout>
            <Seo
                title="Wedding Planning App Pricing UK | First Dance"
                description="Start planning your wedding for free. Upgrade to Diamond (monthly) or Ever After (one payment, lifetime access) for your wedding website, digital RSVPs and guest photo album."
                path="/pricing"
            />
            <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

            <div className="marketing-page">
                <div className="marketing-hero">
                    <p className="marketing-breadcrumb">
                        <Link to="/">Home</Link> &rsaquo; Pricing
                    </p>
                    <h1>Simple pricing for your big day</h1>
                    <p className="marketing-hero-motto">Start free, upgrade when you’re ready</p>
                    <p className="marketing-hero-intro">
                        Every couple starts on the free plan. When you want your wedding website, digital
                        RSVPs and guest photo album, pick the premium plan that suits you — flexible monthly
                        or one single payment.
                    </p>
                </div>
                <div className="hero-divider" />

                <section className="marketing-section">
                    <div className="marketing-container">
                        <div className="pricing-grid">
                            <div className="pricing-card">
                                <img src={freeIcon} alt="First Dance free plan" className="pricing-card-icon" />
                                <h3>Free</h3>
                                <p className="pricing-card-tagline">Get started with the essentials</p>
                                <p className="pricing-price">£0</p>
                                <p className="pricing-price-note">Free forever, no card needed</p>
                                <ul className="pricing-feature-list">
                                    <li>Guest list organisation</li>
                                    <li>Table &amp; seating planner</li>
                                    <li>Menu &amp; meal planner</li>
                                    <li>Wedding day schedule</li>
                                    <li>Planning checklist &amp; to-dos</li>
                                    <li>Digital save the dates</li>
                                    <li className="muted">Personalised wedding website</li>
                                    <li className="muted">Digital RSVPs</li>
                                    <li className="muted">Guest photo album</li>
                                </ul>
                                <a className="pricing-cta secondary" href="/redirect">
                                    Get started free
                                </a>
                            </div>

                            <div className="pricing-card featured">
                                <span className="pricing-badge">Most flexible</span>
                                <img
                                    src={diamondIcon}
                                    alt="First Dance Diamond plan"
                                    className="pricing-card-icon"
                                />
                                <h3>Diamond</h3>
                                <p className="pricing-card-tagline">
                                    Premium features on a flexible monthly plan
                                </p>
                                <p className="pricing-price">
                                    {diamondPrice ? `£${diamondPrice}` : '£9.99'}
                                    <span style={{ fontSize: 18, fontWeight: 700 }}>/month</span>
                                </p>
                                <p className="pricing-price-note">Cancel anytime</p>
                                <ul className="pricing-feature-list">
                                    <li>Everything in Free</li>
                                    <li>Personalised wedding website</li>
                                    <li>Digital RSVPs &amp; meal choices</li>
                                    <li>Guest photo album</li>
                                    <li>Wedding website QR code</li>
                                    <li>Unlimited tables</li>
                                </ul>
                                <a className="pricing-cta" href="/redirect">
                                    Start with the app
                                </a>
                            </div>

                            <div className="pricing-card">
                                <img
                                    src={everAfterIcon}
                                    alt="First Dance Ever After plan"
                                    className="pricing-card-icon"
                                />
                                <h3>Ever After</h3>
                                <p className="pricing-card-tagline">One payment, yours for the journey</p>
                                <p className="pricing-price">
                                    {everafterPrice ? `£${everafterPrice}` : '£59'}
                                </p>
                                <p className="pricing-price-note">One-off payment, no renewals</p>
                                <ul className="pricing-feature-list">
                                    <li>Everything in Diamond</li>
                                    <li>Lifetime access*</li>
                                    <li>No monthly payments</li>
                                    <li>Keep your photo album after the day</li>
                                </ul>
                                <a className="pricing-cta secondary" href="/redirect">
                                    Start with the app
                                </a>
                            </div>
                        </div>

                        <p className="pricing-footnote">
                            *Lifetime access covers your whole planning journey through to at least 6 months
                            after your wedding day, with a single payment. Prices shown in GBP. Already using
                            First Dance? Upgrade in the app under Settings &rarr; Billing.
                        </p>
                    </div>
                </section>

                <section className="marketing-section marketing-section-alt">
                    <div className="marketing-container">
                        <h2>Compare plans</h2>
                        <div className="comparison-table-wrap">
                            <table className="comparison-table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>Free</th>
                                        <th>Diamond</th>
                                        <th>Ever After</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON_ROWS.map((row) => (
                                        <tr key={row.feature}>
                                            <td>{row.feature}</td>
                                            <td>{row.free ? '✓' : '—'}</td>
                                            <td>{row.premium ? '✓' : '—'}</td>
                                            <td>{row.premium ? '✓' : '—'}</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>Lifetime access</td>
                                        <td>—</td>
                                        <td>—</td>
                                        <td>✓</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <section className="marketing-section">
                    <h2>Pricing questions</h2>
                    <div className="faq-section">
                        {PRICING_FAQS.map((faq, index) => (
                            <div
                                key={faq.q}
                                className={openFaqIndex === index ? 'faq-item faq-item-open' : 'faq-item'}
                                onClick={() => toggleFaq(index)}
                            >
                                <div className="faq-item-header">
                                    <h3>{faq.q}</h3>
                                    <img
                                        src={ChevronDown}
                                        alt=""
                                        className={`faq-chevron ${openFaqIndex === index ? 'open' : ''}`}
                                    />
                                </div>
                                <p className={openFaqIndex === index ? 'faq-answer-open' : 'faq-answer-closed'}>
                                    {faq.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="marketing-cta-band">
                    <h2>Start planning today, free</h2>
                    <p>The fun way to plan your wedding day</p>
                    <StoreBadges />
                </div>
            </div>
        </MarketingLayout>
    )
}
